import React, { useEffect, Fragment, useReducer } from 'react';
import { Row, Col, Form, FormGroup, FormControl, Button, Table } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Currency } from '../lib/components/Currency';
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from '../lib/components/LoadingSpinner';
import { ContractLayout } from './ContractLayout';
import { withHeaderMessages } from '../higherOrderComponents/WithHeaderMessages';

const ContractDisbursementQuote = (props) => {

    const initialState = {
        isContractLoading: true,
        isDisbursementLoading: true,
        isDisbursementRequestSubmitted: false,
        documents: {},
        fileUploads: {
            file1: null
        },
        disbursementSelections: {},
        quote: {},
        contract: {
            provider: {
                address: {}
            },
            purchaser: {
                address: {}
            },
            recipients: []
        }
    }

    const reducer = (state, action) => {
        switch (action.type) {
            case 'contractDetails':
                return {
                    ...state,
                    contract: action.payload,
                    isContractLoading: false
                }
            case 'disbursementSelections': 
                return {
                    ...state,
                    disbursementSelections: action.payload
                }
            case 'quote':
                return {
                    ...state,
                   quote: action.payload
                }
            case 'removeFileUpload': 
                return {
                    ...state,
                    fileUploads: action.payload
                }
            case 'cancelFileUpload': 
                return {
                    ...state,
                    fileUploads: action.payload
                }
            case 'uploadAdditionalFile': 
                return {
                    ...state,
                    fileUploads: {
                        ...state.fileUploads,
                        ['file' + action.payload]: null
                    }
                }
            case 'documentSelected': 
                return {
                    ...state,
                    documents: {
                        ...state.documents,
                        [action.payload.document]: (action.payload.checked) ? action.payload.document : undefined
                    }
                }
            case 'fileSelected': 
            return {
                ...state,
                fileUploads: {
                    ...state.fileUploads,
                    [action.payload.file]: action.payload.files[0]
                }
            }
            case 'isContractLoading':
                return {
                    ...state,
                    isContractLoading: action.payload
            }
            case 'isDisbursementLoading':
                return {
                    ...state,
                    isDisbursementLoading: action.payload
                }
            case 'disbursementRequest':
                return {
                    ...state,
                    isDisbursementRequestSubmitted: true,
                    isDisbursementLoading: false
                }
            case 'hideLoader': 
                return {
                    ...state,
                    isContractLoading: false,
                    isDisbursementLoading: false
                }
            default:
                throw new Error();
        }
    }

    const [disbursementQuoteState, dispatch] = useReducer(reducer, initialState);
    const { number } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        async function getContractDetails() {
            props.header.setHeader("contractQuote");
            const res = await props.api.get(`/api/contract/${number}`);
            dispatch({ type: 'contractDetails', payload: res.data});
            
            if (!location.state || !location.state.disbursementSelections) {
                navigate('/contracts');
            }
            dispatch({ type: 'disbursementSelections', payload: location.state.disbursementSelections })
        }
        getContractDetails();
    }, [])

    useEffect(() => {
        async function getDisbursementQuote() {
            const postBody = {
                contractNumber: number,
                reason: location.state.disbursementSelections.reason,
                terminationDate: location.state.disbursementSelections.terminationDate,
                valueOfService: location.state.disbursementSelections.valueOfService,
                includedPurchaseIds: location.state.disbursementSelections.purchases.map(p => p.purchaseId) 
            }
    
            const res = await props.api.post('/api/contract/disbursement-quote', postBody);
            dispatch({ type: 'quote', payload: res.data });
            hideLoader();
        }
        getDisbursementQuote();
    }, [])

    const removeFileUpload = (file, event) =>
    {
        const { fileUploads } = disbursementQuoteState;
        var newFiles = fileUploads;
        delete newFiles[file]

        dispatch({ type: 'removeFileUpload', payload: newFiles });
    }

    const cancelAdditionalFileUpload = (file, event) =>
    {
        const { fileUploads } = disbursementQuoteState;
        var newFiles = fileUploads;
        delete newFiles[file]

        dispatch({ type: 'cancelFileUpload', payload: newFiles });
    }

    const uploadAdditionalFile = (event) =>
    {
        const { fileUploads } = disbursementQuoteState;
        const nextFileNumber = Math.max(...Object.keys(fileUploads).map(k=>k.replace('file','')))+1;

        dispatch({ type: 'uploadAdditionalFile', payload: nextFileNumber });
    }


    const handleDocumentSelected = (document, event) =>
    {
        var { checked } = event.target;
        dispatch({ type: 'documentSelected', payload: { document, checked } })
    }

    const handleFileSelected = (file, event) => {
        const { files } = event.target;
        dispatch({ type: 'fileSelected', payload: { files, file } })
    };

    const disbursementRequestIsValid = () => {
        const { documents, fileUploads } = disbursementQuoteState;
        const { requiredDocuments } = disbursementQuoteState.quote;

        let documentMissing = false;
        let fileUploadMissing = false;
        if (requiredDocuments)
        {
            requiredDocuments.forEach(rd=>
            {
                if (Object.keys(documents).length === 0 || typeof documents[rd] === 'undefined' )
                {
                    documentMissing = true;
                }
            });
            if (requiredDocuments.length > 0)
            {
                if (Object.keys(fileUploads).filter(f=>fileUploads[f] != null).length === 0)
                {
                    fileUploadMissing = true;
                }
            }
        }
        return !documentMissing && !fileUploadMissing;
    };

    const handleDisbursementRequestSubmitted = async (event) => {
        event.preventDefault();
        dispatch({ type: 'isDisbursementLoading', payload: true});

        const { contractNumber  } = disbursementQuoteState.quote;
        const { fileUploads } = disbursementQuoteState;
        const { valueOfService, terminationDate, reason, purchases } = disbursementQuoteState.disbursementSelections;
        
        let formData = new FormData();
        formData.append("contractNumber", contractNumber);
        formData.append("valueOfService", valueOfService);
        formData.append("reason", reason);
        formData.append("terminationDate", terminationDate);
        purchases.forEach(p => {
            formData.append("includedPurchaseIds", p.purchaseId);
        });

        Object.keys(fileUploads).forEach(f => {
            formData.append(f, fileUploads[f], fileUploads[f].name);
        });

        await props.api.postForm('/api/contract/disbursement', formData)
        .then(response => {
            if (response.status === 200) { 
                dispatch({ type: 'disbursementRequest', action: true });
                dispatch({ type: 'isDisbursementLoading', action: false });
            }
        })
        .catch(hideLoader)
        .finally(hideLoader);
    };

    const hideLoader = () => {
        dispatch({ type: 'isContractLoading', payload: false })
        dispatch({ type: 'isDisbursementLoading', payload: false })
    };
    const { quote, isDisbursementRequestSubmitted, fileUploads, contract } = disbursementQuoteState;

    if (disbursementQuoteState.isContractLoading || disbursementQuoteState.isDisbursementLoading) {
        return <LoadingSpinner />;
    }

    if (isDisbursementRequestSubmitted)
    {
        return (
            <ContractLayout contractNumber={number} contractStatus={contract.status}>
                <Fragment>
                    <hr className="page-break" />
                    <Row className="section border-top border-4">
                        <Col sm={12} className="contract-details">
                            <h2 style={{marginTop: "0", color: "#6C6C6D", marginBottom: "30px"}}>
                                Your Distribution Request has been successfully submitted.
                            </h2>
                            <Row>
                                <Col sm={5} style={{marginBottom: "15px"}}>
                                        <div className="label-value">
                                            <span>Recipient Name(s):</span>
                                            {quote.recipients.join(',')}
                                        </div>

                                        <div className="label-value">
                                            <span>Contract Number:</span>
                                            {quote.contractNumber}
                                        </div>
                                        <div className="label-value">
                                            <span>Distribution Amount:</span>
                                                <Currency amount={quote.guaranteedPrincipal + quote.guaranteedIncome
                                                    + quote.nonGuaranteedPrincipal + quote.nonGuaranteedIncome } />
                                        </div>
                                </Col>
                                <Col sm={7}>
                                    <p>
                                        If all appropriate documentation has been received, we will 
                                        process your request within 48-72 hours.  Otherwise, we will contact you
                                        regarding the additional information needed to process the Distribution
                                        Request.
                                    </p>
                                    <p>
                                        If you have any questions, you can contact our Customer Service Team at (800)763-0234.
                                    </p>

                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Fragment>
            </ContractLayout>
        );
    }

    return (
        <ContractLayout contractNumber={number} pageTitle="Distribution Quote" contractStatus={contract.status}>
            <Fragment>
            <hr className="page-break" />
            <Row className='border-top border-4 pt-4'>
                <Col sm={7}>
                    <Table className="dq-table table-borderless">
                        <thead>
                            <tr>
                            <th></th>
                            <th style={{fontWeight: "bold"}}>Principal</th>
                            <th style={{fontWeight: "bold"}}>Income</th>
                            <th style={{fontWeight: "bold"}}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                            <td style={{color: "#6C6C6D"}}>
                                Total Guaranteed
                            </td>
                            <td>
                                <Currency amount={quote.guaranteedPrincipal} />
                            </td>
                            <td>
                                <Currency amount={quote.guaranteedIncome} />
                            </td>
                            <td>
                                <Currency amount={quote.guaranteedPrincipal + quote.guaranteedIncome} />
                            </td>
                            </tr>
                            <tr>
                            <td style={{color: "#6C6C6D"}}>
                                Total Non-Guaranteed
                            </td>
                            <td>
                                <Currency amount={quote.nonGuaranteedPrincipal} />
                            </td>
                            <td>
                                <Currency amount={quote.nonGuaranteedIncome} />
                            </td>
                            <td>
                                <Currency amount={quote.nonGuaranteedPrincipal + quote.nonGuaranteedIncome} />
                            </td>
                            </tr>
                            <tr>
                            <td style={{color: "#6C6C6D"}}>
                                Contract Total
                            </td>
                            <td>
                                <span style={{color: "#d92328"}}><Currency amount={quote.guaranteedPrincipal + quote.nonGuaranteedPrincipal} /></span>
                            </td>
                            <td>
                                <span style={{color: "#d92328"}}><Currency amount={quote.guaranteedIncome + quote.nonGuaranteedIncome} /></span>
                            </td>
                            <td>
                                <span style={{color: "#d92328"}}>
                                    <Currency amount={quote.guaranteedPrincipal + quote.nonGuaranteedPrincipal + quote.guaranteedIncome + quote.nonGuaranteedIncome} /></span>
                            </td>
                            </tr>
                        </tbody>
                    </Table>
                    <br />

                    <Table className="table-sm dq2-table table-borderless">
                        <thead>
                        <tr>
                            <th style={{fontWeight: "bold"}}>Who to Pay</th>
                            <th style={{fontWeight: "bold"}}>Principal</th>
                            <th style={{fontWeight: "bold"}}>Income</th>
                            <th style={{fontWeight: "bold"}}>Payment Method</th>
                        </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Provider: {quote.providerCode} - {quote.providerName}</td>
                                <td><Currency amount={quote.principalToProvider} /></td>
                                <td><Currency amount={quote.incomeToProvider} /></td>
                                <td>{quote.providerPaymentMethod}</td>
                            </tr>
                            {(quote.principalToBeneficiaries !== 0 || quote.incomeToBeneficiaries !== 0) &&
                            <tr>
                                <td>Beneficiary: {quote.beneficiary}</td>
                                <td><Currency amount={quote.principalToBeneficiaries} /></td>
                                <td><Currency amount={quote.incomeToBeneficiaries} /></td>
                                <td>{quote.beneficiaryPaymentMethod}</td>
                            </tr>
                            } 
                            {(quote.principalToPerpetualCare !== 0 || quote.incomeToPerpetualCare !== 0) &&
                            <tr>
                                <td>Perpetual Care</td>
                                <td><Currency amount={quote.principalToPerpetualCare} /></td>
                                <td><Currency amount={quote.incomeToPerpetualCare} /></td>
                                <td>{quote.perpetualCarePaymentMethod}</td>
                            </tr>
                            }

                        </tbody>
                    </Table>
                </Col>
                <Col sm={5}>
                    <Form onSubmit={handleDisbursementRequestSubmitted}>
                        <div className="card panel-default dr-panel">
                            <div className="card-header" style={{backgroundColor: "none"}}>DISTRIBUTION REQUEST   
                                <div className="documents-received">Required Documents:</div>
                            </div>
                            <div className="card-body">
                                {quote.requiredDocuments && quote.requiredDocuments.map((d, i) => (
                                    <div>
                                    <FormGroup style={{display:"inline-flex"}}>
                                        <Form.Check
                                            onChange={(event)=>handleDocumentSelected(d,event)}
                                            name={d}
                                        >
                                        </Form.Check>&nbsp;
                                        <Form.Label>{d}</Form.Label>
                                    </FormGroup>
                                    </div>
                                ))}

                                {(disbursementQuoteState.quote.hideSubmit !== true) &&
                                    <div>
                                        <div className="documents-received">Upload Document(s):</div>
                                        <div>
                                            {quote.requiredDocuments 
                                                && quote.requiredDocuments.length > 0 
                                                && fileUploads 
                                                && Object.keys(fileUploads).map((d, i) => (
                                                <Fragment key={d}>
                                                <FormGroup  style={{display:"inline-flex"}}>
                                                    <FormControl
                                                        type="file"
                                                        name={d}
                                                        onChange={(event) => handleFileSelected(d, event)} />
                                                </FormGroup>
                                                { fileUploads[d] == null && Object.keys(fileUploads).length > 1 &&
                                                    <Button onClick={(event)=>cancelAdditionalFileUpload(d,event)}>Cancel</Button>
                                                }
                                                { fileUploads[d] != null && Object.keys(fileUploads).length > 1 &&
                                                    <Button onClick={(event)=>removeFileUpload(d,event)}>Remove</Button>
                                                }

                                                <br />
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                }
                                { (Object.keys(fileUploads).filter(f=>fileUploads[f] == null).length === 0) &&
                                    <Fragment>
                                        <Button variant='secondary' onClick={uploadAdditionalFile}>Upload Another</Button>
                                    <br/>
                                    </Fragment>
                                }
                                {(disbursementQuoteState.quote.hideSubmit === true) &&
                                    <div>User settings do not allow submitting distribution requests</div>
                                }
                                {(disbursementQuoteState.quote.hideSubmit !== true) &&
                                    <button type="submit" className='btn btn-primary' style={{marginTop: "10px"}}
                                        disabled={!disbursementRequestIsValid()}>Submit Request</button>
                                }
                            </div>
                        </div>
                    </Form>
                </Col>
            </Row>

            </Fragment>
        </ContractLayout>
    );
}

export default withHeaderMessages(WithApiAccess(ContractDisbursementQuote));